import { reservationCommentZodSchema } from "@@/shared/acquire/resources/reservation-comment";
import { spreadTimeFactory } from "@@/shared/acquire/resources/to-resource-reservation-requests";
import { cartIdZodSchema } from "@@/shared/carts/cart-id";
import { iCalImportIdZodSchema } from "@@/shared/channels/ical/ical-import-id";
import type { DateRange } from "@@/shared/date-time/date-range";
import {
    dateRangeFactory,
    dateRangeUtils,
} from "@@/shared/date-time/date-range";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import { orderIdZodSchema } from "@@/shared/orders/order-id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import type { Translatable } from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import { unitZodSchema } from "@@/shared/units-and-quantities/unit";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { sum } from "@@/utilities/sum";
import { unique } from "@@/utilities/unique";
import { filter, pipe } from "remeda";
import type { Except } from "type-fest";
import { z } from "zod";
import { bookableSettingsZodSchema } from "../bookable-settings";
import { bookableOptionValueZodSchema } from "../resource-reference-options/bookable-option-value-zod-schemas";
import { isQuantityPickerBookableOptionValue } from "../resource-reference-options/quantity-picker-bookable-option-value";
import { bookableSessionIdZodSchema } from "../sessions/bookable-session-id";
import type { Resource } from "./resource";
import type { ResourceGroupType } from "./resource-group";
import type { ResourceId } from "./resource-id";
import { resourceIdZodSchema } from "./resource-id";
import {
    resourceReservationIdFactory,
    resourceReservationIdZodSchema,
} from "./resource-reservation-id";
import {
    nonGroupResourceTypeZodEnum,
    type ResourceType,
} from "./resource-type";

const resourceReservationStatus = [
    "PENDING",
    "RESERVED",
    "CANCELLED",
    "REMOVED",
] as const;
const resourceReservationStatusZodEnum = z.enum(resourceReservationStatus);
type ResourceReservationStatus = (typeof resourceReservationStatus)[number];

const resourcePickedBy = ["USER", "SYSTEM"] as const;
const resourcePickedByZodEnum = z.enum(resourcePickedBy);
type ResourcePickedBy = (typeof resourcePickedBy)[number];

type ReservableResourceType = Exclude<ResourceType, ResourceGroupType>;

const _reservationZodObject = z.object({
    _id: resourceReservationIdZodSchema,
    _type: z.literal("RESOURCE_RESERVATION"),
    _createdAt: isoAndUnixZodSchema,
    status: resourceReservationStatusZodEnum,

    // Time
    timeRange: timeRangeZodSchema,

    /**
     * Dynamic time range, contains the actual time range of the
     * reservation with preparation time and postTime time
     * */
    spreadTime: timeRangeZodSchema.optional(),

    // Resource connection
    resourceId: resourceIdZodSchema,
    resourceType: nonGroupResourceTypeZodEnum,
    resourceUnit: unitZodSchema,

    // Quantity
    quantity: z.number().min(1).int(),
    main: z.boolean(),

    sessionId: bookableSessionIdZodSchema.optional(),
    optionValues: z.array(bookableOptionValueZodSchema),
    bookableSettings: bookableSettingsZodSchema.optional(),
    userId: userIdZodSchema,

    pickedBy: resourcePickedByZodEnum,
    cartId: cartIdZodSchema.optional(),
    displayResourceTitle: z.boolean().optional(),

    /** Used by table group */
    parentReservationId: resourceReservationIdZodSchema.optional(),

    comments: z.array(reservationCommentZodSchema).optional(),

    source: z.never().optional(),
    subType: z.never().optional(),
    orderId: z.never().optional(),
    description: z.never().optional(),
    productId: z.never().optional(),
    providerId: providerIdZodSchema.optional(), //! CHANGE THIS AFTER DATA MIGRATION WHEN ALL HAVE IT
});

const dropInReservationZodObject = _reservationZodObject.extend({
    type: z.literal("DROP_IN"),
});
type DropIn_ResourceReservation = z.infer<typeof dropInReservationZodObject>;

const blockedReservationZodObject = _reservationZodObject.extend({
    type: z.literal("BLOCKED"),
});
type Blocking_ResourceReservation = z.infer<typeof blockedReservationZodObject>;
const externalThirdPartySources = ["sirvoy", "generic", "destio"] as const;
const externalThirdPartySourceZodEnum = z.enum(externalThirdPartySources);
type ExternalThirdPartySource = (typeof externalThirdPartySources)[number];
const externalThirdPartyOrder_blockedReservationZodObject =
    _reservationZodObject.extend({
        type: z.literal("BLOCKED"),
        subType: z.literal("EXTERNAL_THIRD_PARTY_ORDER"),
        /** third party system */
        source: externalThirdPartySourceZodEnum,
        /** Order Id in the external third party system */
        externalOrderId: z.string().optional(),
        /** Name of the resource in the external third party system */
        externalResourceName: z.string().optional(),
        /** Description of the order from the third party system */
        description: z.string().optional(),
        /**
         * All blockings imported from an external system
         * at the same time have the same import id
         */
        importId: z.string().optional(),
        orderId: z.never().optional(),
    });
type ExternalThirdPartyOrderBlocking_ResourceReservation = z.infer<
    typeof externalThirdPartyOrder_blockedReservationZodObject
>;
const externalICal_blockedReservationZodObject = _reservationZodObject.extend({
    type: z.literal("BLOCKED"),
    subType: z.literal("EXTERNAL_ICAL"),
    iCalId: iCalImportIdZodSchema,
    /** third party system */
    source: z.enum(["generic", "booking", "airbnb", "sirvoy", "destio"]),
    /**
     * External id from the iCal import (UID from the iCal file)
     */
    externalId: z.string(),
    /** Description of the order from the third party system */
    description: z.string().optional(),
});
type ExternalICalBlocking_ResourceReservation = z.infer<
    typeof externalICal_blockedReservationZodObject
>;
const regularReservationZodObject = _reservationZodObject.extend({
    type: z.never().optional(),
    subType: z.never().optional(),
    /**
     * Order id kan vara undefined när typen är `BLOCKED`, `DROP_IN` eller sub-typen `EXTERNAL_THIRD_PARTY_ORDER`
     * Blockeringar och drop-ins är inte skapade utifrån en order och har därför inget order-id
     */
    orderId: orderIdZodSchema,
    productId: productIdZodSchema,
});
type Regular_ResourceReservation = z.infer<typeof regularReservationZodObject>;

const resourceReservationZodUnion = z.union([
    dropInReservationZodObject,
    blockedReservationZodObject,
    externalThirdPartyOrder_blockedReservationZodObject,
    externalICal_blockedReservationZodObject,
    regularReservationZodObject,
]);
type ResourceReservation = z.infer<typeof resourceReservationZodUnion>;

// type ResourceReservation = {
//     readonly _id: ResourceReservationId;
//     readonly _type: "RESOURCE_RESERVATION";
//     readonly _createdAt: IsoAndUnixTimestamp;
//     readonly type?: never;
//     // readonly subType?: "EXTERNAL_THIRD_PARTY_ORDER" | "EXTERNAL_ICAL";

//     readonly status: "PENDING" | "RESERVED" | "CANCELLED" | "REMOVED";

//     readonly timeRange: TimeRange;
//     //Dynamic time range, contains the actual time range of the reservation
//     //with preparation time and postTime time
//     readonly spreadTime?: TimeRange;

//     readonly resourceId: ResourceId;

//     readonly quantity: number;
//     readonly main: boolean;
//     readonly resourceType: ReservableResourceType;
//     readonly resourceUnit: Unit;

//     readonly sessionId?: BookableSessionId;
//     readonly optionValues: BookableOptionValue[];
//     readonly bookableSettings?: BookableSettings;
//     readonly userId: UserId;
//     /**
//      * Order id kan vara undefined när typen är `BLOCKED`, `DROP_IN` eller sub-typen `EXTERNAL_THIRD_PARTY_ORDER`
//      * Blockeringar och drop ins är inte skapade utifrån en order
//      */
//     readonly orderId: OrderId;

//     readonly pickedBy: "USER" | "SYSTEM";
//     readonly cartId?: CartId;
//     readonly displayResourceTitle?: boolean;

//     //Used by table group
//     readonly parentReservationId?: ResourceReservationId;

//     readonly source?: never;

//     //readonly accommodationType?: PickedAccommodationType; //commented out because it is not used remove after 2024-70-07
// } & (
//     | {
//         readonly type: "BLOCKED" | "DROP_IN";
//         readonly subType?: never;
//         readonly orderId?: never;
//     }
//     | {
//              readonly type: "BLOCKED";
//           readonly subType: "EXTERNAL_THIRD_PARTY_ORDER";
//           /** third party system */
//           readonly source: "sirvoy" | "generic";
//           /** Order Id in the external third party system */
//           readonly externalOrderId: string;
//           /** Name of the resource in the external third party system */
//           readonly externalResourceName?: string;
//           /** Description of the order from the third party system */
//           readonly description?: string;
//           /**
//            * All blocks of an external order
//            * imported at the same time
//            * have the same import id
//            */
//           readonly importId?: string;
//           readonly comments?: ReservationComment[];
//           readonly orderId: never
//       }
//     | {
//             readonly type: "BLOCKED";
//           readonly subType: "EXTERNAL_ICAL";
//           /** third party system, try to parse data */
//           readonly source: "generic" | "booking" | "airbnb" | "sirvoy";

//           readonly iCalId: ICalImportId;
//           /**
//            * External id from the iCal import (UID from the iCal file)
//            */
//           readonly externalId: string;
//           /** Description of the order from the third party system */
//           readonly description?: string;

//           /** Parsed external order id if it can be found */
//           readonly externalOrderId?: string;
//           readonly comments?: ReservationComment[];
//           readonly orderId: never
//       }
//     | {
//           /** Defaults to never/undefined */
//           readonly subType?: never;
//           /**
//            * When undefined or destio, block is
//            * of local origin from our own system.
//            * No references to any third party system
//            */
//           readonly source?: never;
//           /** Can only be set when subType is `EXTERNAL_THIRD_PARTY_ORDER`. */
//           readonly externalOrderId?: never;
//           /** Can only be set when subType is `EXTERNAL_THIRD_PARTY_ORDER`. */
//           readonly externalResourceName?: never;
//           readonly description?: never;
//           readonly comments?: ReservationComment[];
//           readonly orderId: OrderId
//       }
// );

const resourceReservationFactory = {
    dropIn: (
        params: Except<
            DropIn_ResourceReservation,
            "_type" | "_id" | "_createdAt" | "type"
        >,
    ): DropIn_ResourceReservation => {
        return {
            ...params,
            _id: resourceReservationIdFactory(),
            _type: "RESOURCE_RESERVATION",
            type: "DROP_IN",
            _createdAt: nowIsoAndUnix(),
            spreadTime: spreadTimeFactory({
                settings: params.bookableSettings,
                time: params.timeRange,
            }),
        };
    },
    blocking: {
        regular: (
            params: Except<
                Blocking_ResourceReservation,
                "_type" | "_id" | "_createdAt" | "type"
            >,
        ): Blocking_ResourceReservation => {
            return {
                ...params,
                _id: resourceReservationIdFactory(),
                _type: "RESOURCE_RESERVATION",
                _createdAt: nowIsoAndUnix(),
                type: "BLOCKED",
                spreadTime: spreadTimeFactory({
                    settings: params.bookableSettings,
                    time: params.timeRange,
                }),
            };
        },
        externalThirdPartyOrder: (
            params: Except<
                ExternalThirdPartyOrderBlocking_ResourceReservation,
                "_type" | "_id" | "_createdAt" | "type" | "subType"
            >,
        ): ExternalThirdPartyOrderBlocking_ResourceReservation => {
            return {
                ...params,
                _id: resourceReservationIdFactory(),
                _type: "RESOURCE_RESERVATION",
                _createdAt: nowIsoAndUnix(),
                type: "BLOCKED",
                subType: "EXTERNAL_THIRD_PARTY_ORDER",
                spreadTime: spreadTimeFactory({
                    settings: params.bookableSettings,
                    time: params.timeRange,
                }),
            };
        },
        externalIcalOrder: (
            params: Except<
                ExternalICalBlocking_ResourceReservation,
                "_type" | "_id" | "_createdAt" | "type" | "subType"
            >,
        ): ExternalICalBlocking_ResourceReservation => {
            return {
                ...params,
                _id: resourceReservationIdFactory(),
                _type: "RESOURCE_RESERVATION",
                _createdAt: nowIsoAndUnix(),
                type: "BLOCKED",
                subType: "EXTERNAL_ICAL",
                spreadTime: spreadTimeFactory({
                    settings: params.bookableSettings,
                    time: params.timeRange,
                }),
            };
        },
    },

    regular: (
        params: Except<
            Regular_ResourceReservation,
            "_type" | "_id" | "_createdAt"
        >,
    ): Regular_ResourceReservation => {
        return {
            ...params,
            _id: resourceReservationIdFactory(),
            _type: "RESOURCE_RESERVATION",
            _createdAt: nowIsoAndUnix(),
            spreadTime: spreadTimeFactory({
                settings: params.bookableSettings,
                time: params.timeRange,
            }),
        };
    },
};

const externalResourceReservationSourceMeta: {
    [K in
        | NonNullable<
              ExternalThirdPartyOrderBlocking_ResourceReservation["source"]
          >
        | ExternalICalBlocking_ResourceReservation["source"]]: {
        title: Translatable;
        value: K;
    };
} = {
    destio: {
        title: "Destio",
        value: "destio",
    },
    sirvoy: {
        title: "Sirvoy",
        value: "sirvoy",
    },
    generic: {
        title: translation({
            sv: "Generell",
            en: "Generic",
        }),
        value: "generic",
    },
    booking: {
        title: "Booking.com",
        value: "booking",
    },
    airbnb: {
        title: "Airbnb",
        value: "airbnb",
    },
};

const isResourceReservation = (item: unknown): item is ResourceReservation => {
    return (item as ResourceReservation)?._type === "RESOURCE_RESERVATION";
};

const isBlockedResourceReservation = (
    reservation: unknown,
): reservation is Blocking_ResourceReservation =>
    isResourceReservation(reservation) &&
    reservation.type === "BLOCKED" &&
    !reservation.subType;

const isExternalThirdPartyOrderResourceReservation = (
    reservation: unknown,
): reservation is ExternalThirdPartyOrderBlocking_ResourceReservation =>
    isResourceReservation(reservation) &&
    reservation.type === "BLOCKED" &&
    reservation.subType === "EXTERNAL_THIRD_PARTY_ORDER";
const isExternalICalOrderResourceReservation = (
    reservation: unknown,
): reservation is ExternalICalBlocking_ResourceReservation =>
    isResourceReservation(reservation) &&
    reservation.type === "BLOCKED" &&
    reservation.subType === "EXTERNAL_ICAL";

const isDropInResourceReservation = (
    reservation: unknown,
): reservation is DropIn_ResourceReservation =>
    isResourceReservation(reservation) &&
    reservation.type === "DROP_IN" &&
    !reservation.subType;
const isRegularResourceReservation = (
    reservation: unknown,
): reservation is Regular_ResourceReservation =>
    isResourceReservation(reservation) && !reservation.type;

const getMergedDateRange = (reservations: ResourceReservation[]): DateRange => {
    const dateRanges = reservations
        .map(reservation => reservation.timeRange)
        .map(timeRange => dateRangeFactory.fromTimeRange({ timeRange }));
    const result = dateRangeUtils.mergeDateRanges(dateRanges);
    return result;
};

/**
 * Calculates the total reserved quantity of resources based on the provided reservations and resources.
 *
 * This function processes reservations for both individual tables and table groups, ensuring that each table is only counted once.
 * For table group reservations, it counts the number of unique tables in the group that have not already been reserved.
 * For reservations of type "BLOCKED" or "DROP_IN", it counts each as a single reservation.
 * For other resource types, it sums the quantities from bookable option values.
 *
 * @param reservations - An array of `ResourceReservation` objects representing the current reservations.
 * @param resources - An array of `Resource` objects representing all available resources.
 * @returns The total number of reserved resources as a number.
 * @throws Will throw an error if a table group reservation does not correspond to a valid table group resource.
 */
const countReservedQuantity = (
    reservations: ResourceReservation[],
    resources: Resource[],
): number => {
    const tableResources = pipe(
        resources,
        filter(
            r =>
                r._type === "RESOURCE_TABLE_GROUP" ||
                r._type === "RESOURCE_TABLE",
        ),
    );

    const reservedTablesCounted = new Set<ResourceId>();
    const reservedCount = sum(reservations, reservation => {
        if (reservation.resourceType === "RESOURCE_TABLE") {
            if (reservedTablesCounted.has(reservation.resourceId)) return 0;
            reservedTablesCounted.add(reservation.resourceId);
            return 1;
        }

        if (reservation.resourceType === "RESOURCE_TABLE_GROUP") {
            // Find the table group resource
            const tableGroup = tableResources.find(
                r => r._id === reservation.resourceId,
            );
            if (
                // Make sure it's still a table group
                tableGroup?._type !== "RESOURCE_TABLE_GROUP"
            ) {
                throw new Error("A table group resource was expected");
            }
            const tables = unique(
                tableGroup.resources
                    .map(r => r._id)
                    .filter(r => !reservedTablesCounted.has(r)),
            );

            // Count the number of unique resources in the group
            // and use that as the quantity
            tables.forEach(t => reservedTablesCounted.add(t));
            return tables.length;
        }

        // When blocked or drop in...
        if (reservation.type === "BLOCKED" || reservation.type === "DROP_IN") {
            return reservation.quantity;
        }

        // For other resource types,
        // just return the quantity
        return sum(
            reservation.optionValues.filter(
                isQuantityPickerBookableOptionValue,
            ),
            ov => ov.quantity.value,
        );
    });

    return reservedCount;
};

const resourceReservationUtils = {
    getMergedDateRange,
    isBlockedResourceReservation,
    isDropInResourceReservation,
    isExternalICalOrderResourceReservation,
    isExternalThirdPartyOrderResourceReservation,
    isRegularResourceReservation,
    isResourceReservation,
    countReservedQuantity,
};

export {
    countReservedQuantity,
    externalResourceReservationSourceMeta,
    externalThirdPartySources,
    externalThirdPartySourceZodEnum,
    isBlockedResourceReservation,
    isDropInResourceReservation,
    isExternalICalOrderResourceReservation,
    isExternalThirdPartyOrderResourceReservation,
    isRegularResourceReservation,
    isResourceReservation,
    resourceReservationFactory,
    resourceReservationUtils,
    resourceReservationZodUnion,
};
export type {
    Blocking_ResourceReservation,
    DropIn_ResourceReservation,
    ExternalICalBlocking_ResourceReservation,
    ExternalThirdPartyOrderBlocking_ResourceReservation,
    ExternalThirdPartySource,
    Regular_ResourceReservation,
    ReservableResourceType,
    ResourcePickedBy,
    ResourceReservation,
    ResourceReservationStatus,
};
